import React, { useEffect, useRef } from 'react'
import { BootPhase, BootMetrics } from '@/hooks/useBootPhaseManager'

interface BootLoaderUIProps {
  phases: BootPhase[]
  bootMetrics: BootMetrics
  isBooting: boolean
  audioContextActive?: boolean
  bootAudioRef?: React.RefObject<HTMLAudioElement>
}

export const BootLoaderUI: React.FC<BootLoaderUIProps> = ({
  phases,
  bootMetrics,
  isBooting,
  audioContextActive = false,
  bootAudioRef
}) => {

  // Handle boot audio playback - auto-play with lower volume
  useEffect(() => {
    const bootAudio = bootAudioRef?.current
    if (!bootAudio) return

    // Set lower volume to prevent it being "scrashi"
    bootAudio.volume = 0.3 // 30% volume

    const playBootTune = async () => {
      try {
        // Reset audio to beginning
        bootAudio.currentTime = 0
        // Play the quantum symphony boot tune
        await bootAudio.play()
      } catch (error) {
        // Silently fail if audio can't play (user interaction required, etc.)
        console.warn('Boot audio playback failed:', error)
      }
    }

    // Auto-play the boot tune immediately when component mounts and is booting
    if (isBooting) {
      // Small delay to ensure audio element is ready
      const timer = setTimeout(() => {
        playBootTune()
      }, 100)

      return () => {
        clearTimeout(timer)
        // Cleanup on unmount
        if (bootAudio) {
          bootAudio.pause()
          bootAudio.currentTime = 0
        }
      }
    }

    return () => {
      // Cleanup on unmount
      if (bootAudio) {
        bootAudio.pause()
        bootAudio.currentTime = 0
      }
    }
  }, [isBooting])

  const getStatusColor = (status: BootPhase['status']) => {
    switch (status) {
      case 'completed': return 'text-green-400'
      case 'active': return 'text-blue-400'
      case 'error': return 'text-red-400'
      default: return 'text-gray-400'
    }
  }

  const getStatusIcon = (status: BootPhase['status']) => {
    switch (status) {
      case 'completed': return '✅'
      case 'active': return '🔄'
      case 'error': return '❌'
      default: return '⏳'
    }
  }

  if (!isBooting) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 cursor-default"
      data-testid="boot-loader"
    >
      {/* Boot Audio - Quantum Symphony */}
      <audio
        ref={bootAudioRef}
        src="/audio/quantum_symphony.mp3"
        preload="auto"
        loop={false}
        className="hidden"
        aria-label="Boot sequence audio"
      />

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-blue-400/20 rounded-full animate-ping"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-purple-400/30 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-cyan-400/25 rounded-full animate-bounce"></div>
        <div className="absolute bottom-1/3 right-1/4 w-1 h-1 bg-pink-400/20 rounded-full animate-pulse"></div>
      </div>

      <div className="w-full max-w-5xl mx-4 relative z-10">
        {/* Elegant Header */}
        <div className="text-center mb-12 animate-in fade-in-0 slide-in-from-top-4 duration-1000">
          <div className="relative inline-block mb-6">
            <div className="text-7xl animate-pulse">🎵</div>
            <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent mb-3">
            SOUND WIZARD STUDIO
          </h1>
          <p className="text-xl text-gray-300 font-light">
            Quantum Audio Synthesis Engine
          </p>
          <div className="mt-4 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-400 font-medium">SYSTEM INITIALIZING</span>
          </div>
        </div>

        {/* Phase Progress */}
        <div className="bg-slate-900/60 backdrop-blur-md rounded-2xl p-8 mb-8 border border-slate-700/50 shadow-2xl animate-in fade-in-0 slide-in-from-bottom-4 duration-1000 delay-300">
          <div className="flex items-center justify-center mb-8">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse mr-3"></div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Quantum Initialization Sequence
            </h2>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse ml-3"></div>
          </div>

          <div className="space-y-6">
            {phases.map((phase, index) => (
              <div
                key={phase.id}
                className="group relative bg-slate-800/40 rounded-xl p-5 border border-slate-700/30 hover:border-slate-600/50 transition-all duration-300 animate-in fade-in-0 slide-in-from-left-4"
                /* eslint-disable react/style-prop-object */
                style={{ '--animation-delay': `${index * 150}ms` } as React.CSSProperties}
                /* eslint-enable react/style-prop-object */
              >
                {/* Subtle glow effect for active phase */}
                {phase.status === 'active' && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl animate-pulse"></div>
                )}

                <div className="relative flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      phase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      phase.status === 'active' ? 'bg-blue-500/20 text-blue-400 animate-pulse' :
                      phase.status === 'error' ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-700/50 text-slate-400'
                    } transition-all duration-300`}>
                      <span className="text-xl">{phase.icon}</span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-lg font-semibold ${
                        phase.status === 'completed' ? 'text-green-300' :
                        phase.status === 'active' ? 'text-blue-300' :
                        phase.status === 'error' ? 'text-red-300' :
                        'text-slate-300'
                      }`}>{phase.name}</h3>
                      <div className={`flex items-center space-x-2 ${getStatusColor(phase.status)}`}>
                        <span className="text-lg">{getStatusIcon(phase.status)}</span>
                        <span className="text-sm font-medium">
                          {phase.status === 'active' ? `${Math.round(phase.progress)}%` :
                           phase.status === 'completed' ? 'Complete' :
                           phase.status === 'error' ? 'Failed' : 'Pending'}
                        </span>
                      </div>
                    </div>

                    <div className="text-sm text-slate-400 mb-4 leading-relaxed">{phase.description}</div>

                    <div className="relative">
                      <div className="w-full bg-slate-700/50 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ease-out ${
                            phase.status === 'completed' ? 'bg-gradient-to-r from-green-500 to-green-400' :
                            phase.status === 'active' ? 'bg-gradient-to-r from-blue-500 to-blue-400' :
                            phase.status === 'error' ? 'bg-gradient-to-r from-red-500 to-red-400' :
                            'bg-slate-600'
                          } ${phase.status === 'active' ? 'animate-pulse' : ''}`}
                          /* eslint-disable react/style-prop-object */
                          style={{ '--progress-width': `${phase.progress}%` } as React.CSSProperties}
                          /* eslint-enable react/style-prop-object */
                        />
                      </div>
                      {/* Progress glow */}
                      {phase.status === 'active' && (
                        <div className="absolute inset-0 rounded-full bg-blue-400/20 blur-sm animate-pulse"></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Status */}
        {(bootMetrics.errors.length > 0 || bootMetrics.warnings.length > 0) && (
          <div className="bg-slate-900/60 backdrop-blur-md rounded-2xl p-6 mb-8 border border-slate-700/50 animate-in fade-in-0 slide-in-from-bottom-4 duration-1000 delay-700">
            <div className="flex items-center justify-center mb-6">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse mr-3"></div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                System Diagnostics
              </h3>
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse ml-3"></div>
            </div>

            {bootMetrics.errors.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <h4 className="text-red-300 font-semibold">Critical Issues</h4>
                </div>
                <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4">
                  <ul className="space-y-2">
                    {bootMetrics.errors.map((error, index) => (
                      <li key={index} className="text-red-200 text-sm flex items-start space-x-2">
                        <span className="text-red-400 mt-0.5">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {bootMetrics.warnings.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <h4 className="text-yellow-300 font-semibold">System Warnings</h4>
                </div>
                <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                  <ul className="space-y-2">
                    {bootMetrics.warnings.map((warning, index) => (
                      <li key={index} className="text-yellow-200 text-sm flex items-start space-x-2">
                        <span className="text-yellow-400 mt-0.5">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quantum Status Display */}
        <div className="text-center animate-in fade-in-0 slide-in-from-bottom-4 duration-1000 delay-1000">
          <div className="bg-slate-900/60 backdrop-blur-md rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
            <div className="mb-6">
              <div className="text-slate-300 text-sm font-medium mb-2">
                Quantum Processing Systems
              </div>
              <div className="text-slate-400 text-xs leading-relaxed">
                Initializing advanced neural networks, quantum algorithms, and audio synthesis engines
              </div>
            </div>

            {/* Audio Context Status */}
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  audioContextActive ? 'bg-green-400 animate-pulse' : 'bg-blue-400 animate-pulse'
                }`}></div>
                <span className="text-sm font-medium text-slate-300">
                  {audioContextActive ? 'Audio Engine Active' : 'Audio Engine Ready'}
                </span>
              </div>
            </div>

            {/* Neural Network Status */}
            <div className="mt-6 pt-6 border-t border-slate-700/50">
              <div className="flex items-center justify-center space-x-2 text-xs text-slate-400">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse"></div>
                <span>Neural Networks Loading</span>
                <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></div>
                <span>Quantum Algorithms Initializing</span>
                <div className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}